<template>
  <div
    class="client-grid-file relative w-full h-48 bg-slate-100 border rounded transition-all duration-300"
    :class="{ 'border-light-purple': checked, 'border-slate-400': !checked }"
    :style="
      checked ? { boxShadow: '0px 0px 0px 2px rgba(128, 109, 179, 0.3)' } : {}
    "
  >
    <div
      class="h-full w-full"
      :class="{
        'cursor-pointer': isPreviewAvailable,
        'pointer-events-none': !isPreviewAvailable
      }"
      @click="previewOpened = true"
    >
      <div
        v-if="contentType"
        class="h-38 w-full bg-pure-white flex-shrink-0 flex items-center justify-center overflow-hidden rounded-t"
      >
        <img
          v-if="isPreviewAvailable && contentType.includes('image')"
          :src="document.file_url"
          :alt="document.filename"
          class="w-full h-auto"
        />
        <div
          v-else
          class="h-32 w-32 flex items-center justify-center bg-slate-200 rounded-full"
        >
          <BlIcon icon="file" color="slate-6" class="h-25 w-25" />
        </div>
      </div>
      <div
        class="h-10 w-full px-2 flex items-center rounded-b truncate"
        :title="document.filename"
      >
        <BlTextBody2 :text="document.filename" class="truncate" />
      </div>
    </div>

    <div
      class="action-area absolute top-0 left-0 w-full py-1.5 px-1.5 flex items-center justify-between transition-all duration-300"
      :class="{ 'opacity-100': checked }"
    >
      <div
        class="h-8 w-8 flex items-center justify-center rounded-lg cursor-pointer"
        :style="{ background: 'rgba(239, 239, 239, 0.8)' }"
        @click="$emit('change-selected')"
      >
        <BlInputCheck :checked="checked" @changed="$emit('change-selected')" />
      </div>
      <div
        class="py-1 px-1 flex items-center justify-between gap-1 rounded-lg"
        :style="{ background: 'rgba(239, 239, 239, 0.8)' }"
      >
        <BlInputButton
          icon="delete"
          type="outlined-error"
          dense
          @clicked="deleteOpened = true"
        />
        <BlInputButton
          icon="download"
          type="outlined"
          dense
          :loading="downloadLoading"
          @clicked="onDownload"
        />
      </div>
    </div>

    <BlBackdrop
      v-if="deleteOpened"
      title="Delete file"
      :label="`Delete file`"
      :description="
        `<div class='break-words'>You are about to delete <b>${document.filename}</b> from your client. Are you sure you want to proceed? This action cannot be undone.</div>`
      "
      type="error"
      :submitLoading="deleteLoading"
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onDelete"
    />

    <PreviewModal
      v-if="previewOpened"
      :document="document"
      :checked="checked"
      @change-selected="$emit('change-selected')"
      @after-commit="$emit('after-commit')"
      @close-modal="previewOpened = false"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import PreviewModal from "./PreviewModal.vue";

import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputCheck from "../../BlInputCheck.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlBackdrop from "../../BlBackdrop.vue";

export default {
  name: "FileItemGrid",
  components: {
    PreviewModal,
    BlTextBody2,
    BlIcon,
    BlInputCheck,
    BlInputButton,
    BlBackdrop
  },
  props: {
    document: { type: Object, required: true },
    checked: { type: Boolean, default: false }
    // @change-selected
    // @after-commit
  },
  data() {
    return {
      previewOpened: false,
      deleteOpened: false,
      deleteLoading: false,
      downloadLoading: false
    };
  },
  computed: {
    ...mapGetters(["client"]),
    contentType() {
      return this.document.content_type;
    },
    // audio_types = ['audio/mpeg']
    // image_types = ['image/gif', 'image/jpeg', 'image/png']
    // video_types = ['video/x-flv', 'video/mp4', 'video/MP2T', 'video/3gpp', 'video/quicktime', 'video/x-msvideo', 'video/x-ms-wmv', 'application/x-mpegURL']
    // doc_types   = ['application/msword', 'application/pdf', 'application/vnd.ms-excel', 'application/vnd.ms-powerpoint', 'application/vnd.oasis.opendocument.text',
    //                 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    //                 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/csv', 'text/plain', 'application/rtf',
    //                 'application/vnd.oasis.opendocument.presentation', 'application/vnd.oasis.opendocument.spreadsheet'
    isPreviewAvailable() {
      const types = {
        "svg+xml": false,
        psd: false,
        dwg: false,
        audio: false,
        image: true,
        video: false,
        "x-mpegURL": false,
        pdf: true,
        application: false,
        document: false,
        text: false
      };
      for (const type in types) {
        if (this.contentType.includes(type)) return types[type];
      }
      return false;
    }
  },
  watch: {},
  mounted() {},
  methods: {
    ...mapActions(["deleteClientDocument", "downloadClientDocument"]),
    async onDelete() {
      this.deleteLoading = true;
      const data = {
        client_id: this.client.id,
        ids: this.document.id
      };
      const { success } = await this.deleteClientDocument(data);
      if (!success) {
        this.deleteLoading = false;
        return;
      }
      if (this.checked) {
        this.$emit("change-selected");
      }
      await this.$emit("after-commit");
      this.deleteLoading = false;
      this.deleteOpened = false;
    },
    onDownload() {
      this.downloadLoading = true;
      const data = {
        client_id: this.client.id,
        ids: this.document.id
      };
      this.downloadClientDocument(data)
        .then(res => {
          this.downloadLoading = false;
          const href = URL.createObjectURL(res.data);
          const filename = this.document.filename;

          const link = document.createElement("a");
          link.href = href;
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          URL.revokeObjectURL(href);
        })
        .catch(err => {
          console.error(err);
          this.downloadLoading = false;
        });
    }
  }
};
</script>

<style lang="scss">
.client-grid-file {
  .action-area {
    opacity: 0;
  }
  &:hover {
    .action-area {
      opacity: 1;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/clients/file/ItemGrid.vue